import { useCallback, useMemo } from 'react';
import cx from 'classnames';

import { MDText } from 'i18n-react';

import { Badge, BankIcon, IconComponentFunction, IconComponentProps, IconText, InfoIcon } from '@motorway/mw-highway-code';
import { BADGE_VARIANT } from '@motorway/mw-highway-code/enums';
import { THCComponentProps } from '@motorway/mw-highway-code/src/types';
import { filterDataProps } from '@motorway/mw-highway-code/src/utils/helpers';

import { formatPriceTransactional } from 'Utilities/formatPrice';

import { ServiceFeeAppliesNotice } from 'Components/transactionFees/ServiceFeeAppliesNotice/ServiceFeeAppliesNotice';
import { OverpaymentInfoBox } from 'Containers/app/postSale/financeSettlement/components/PaymentInformationCard/PaymentInformationCard';
import { NegativeEquityTerm } from 'Containers/app/vehicleCollection/components/NegativeEquityTerms/NegativeEquityTerm';
import { SaleDetailsError } from 'Containers/app/vehicleCollection/components/SaleDetailsError/SaleDetailsError';
import { useSaleDetailsSub } from 'Containers/app/vehicleCollection/hooks/useSaleDetailsSub';
import { validateFigure, validateHasTransactionalValue } from 'Containers/app/vehicleCollection/utils/validator';
import { SaleDetailsState } from 'Containers/app/vehicleCollection/VehicleCollectionSubjects.types';
import { Tooltip } from 'Storybook/components/local/Tooltip/Tooltip';

import { ServiceFeeTiersResponse } from '../../../../../../../../server/views/serviceFees/serviceFees.types';
import Texts from '../../SaleDetails.texts.json';
import { SaleDetailsContentProps } from '../../SaleDetails.types';
import { SaleDetailsItem } from '../../SaleDetailsItem';
import { VehicleCard } from '../VehicleCard/VehicleCard';

import styles from './SaleDetailsContentViewWithServiceFee.module.scss';

export type SaleDetailsContentViewProps = THCComponentProps<Prettify<{
  bankTransferFigure: SaleDetailsState['neBankTransferTotalAmount'];
  financeFigure: SaleDetailsState['financingAmount'];
  hasServiceFeePreDeducted?: boolean;
  isServiceFeeEnabled?: boolean;
  mode?: 'negativeEquity' | 'positiveEquity' | 'saleComplete';
  sellerFigure: SaleDetailsState['sellerAmount'];
  serviceFeeNoticeProps?: { feeAmountGBP?: number | null; serviceFeeTiers?: ServiceFeeTiersResponse | null };
  showAsError?: boolean;
  showHomeCollectionLineItem?: boolean;
  showOverpaymentMessage?: boolean;
  showPayoutDetails?: boolean;
  showVehicleCard?: boolean;
  soldForFigure: SaleDetailsState['soldForFigure'];
} & Pick<SaleDetailsContentProps, 'title' | 'titleIcon' | 'variant'>>>

const localT = new MDText(Texts);

const SellerAmountFormattedIcon: IconComponentFunction = (props: IconComponentProps) => (
  <span className={styles.totalTooltipWrapper}>
    <Tooltip
      maxWidth={245}
      overlay={<div>{localT.translate('tooltips.estimatedPayout')}</div>}
      placement="top"
    >
      <InfoIcon {...props} filled={false} size="xsmall" />
    </Tooltip>
  </span>
);

const CardShell = ({
  children,
  title,
  titleIcon,
}: {
  children: React.ReactNode;
  title?: React.ReactNode;
  titleIcon?: IconComponentFunction;
}) => (
  <div className={styles.cardShell}>
    {title && (
      <h2 className={styles.title}>
        {titleIcon ? <IconText icon={titleIcon} text={title} variant='small--heading5'/> : title}
      </h2>
    )}
    {children}
  </div>
);

const PayoutDetails = ({ showPayoutDetails }: { showPayoutDetails: boolean }) => {
  const {
    accountNumber,
    hasNegativeEquity,
  } = useSaleDetailsSub();

  if (!showPayoutDetails || !accountNumber || hasNegativeEquity) {
    return null;
  }

  return (
    <div className={styles.payoutDetailsServiceFee}>
      <BankIcon filled={false} size="xsmall" />
      <span className={styles.payoutDetails}>
        {localT.translate('labels.accountEndingShort', { accountEnding: accountNumber })}
      </span>
    </div>
  );
};

const CardContent = ({
  lineItems,
  showOverpaymentMessage,
  showVehicleCard,
  variant = 'card',
}: {
  lineItems: Array<{
    className?: cx.Argument;
    id: string;
    node: JSX.Element;
    shouldShow: string | boolean | undefined;
  }>;
  showOverpaymentMessage: boolean;
  showPayoutDetails: boolean;
  showVehicleCard: boolean;
  variant?: SaleDetailsContentProps['variant'];
}) => (
  <>
    <div className={cx(styles.cardContent, styles[variant])}>
      {showVehicleCard ? <VehicleCard /> : null}
      <ul className={styles.amountList}>
        {lineItems.map(
          ({ className, id, node, shouldShow }) =>
            (shouldShow ? (
              <li key={id} className={cx(styles.amountListItem, className)} data-item={id}>
                {node}
              </li>
            ) : null),
        )}
      </ul>
    </div>
    <NegativeEquityTerm />
    {showOverpaymentMessage ? <OverpaymentInfoBox size='small' /> : null}
  </>
);

export const SaleDetailsContentViewWithServiceFee = (props: SaleDetailsContentViewProps) => {
  const {
    bankTransferFigure,
    financeFigure,
    hasServiceFeePreDeducted,
    mode = 'positiveEquity',
    sellerFigure,
    serviceFeeNoticeProps,
    showAsError = false,
    showHomeCollectionLineItem = false,
    showOverpaymentMessage = false,
    showPayoutDetails = false,
    showVehicleCard = true,
    soldForFigure,
    title,
    titleIcon,
    variant = 'card',
  } = props;

  const dataProps = filterDataProps(props);

  const { feeAmountGBP, serviceFeeTiers } = serviceFeeNoticeProps ?? {};
  const isFeeLoaded = feeAmountGBP != null;

  const isNearNegativeEquity = (sellerFigure! - (feeAmountGBP ?? 0)) <= 0;

  const getServiceFeeMultiplier = useCallback(() => {
    let serviceFeeMultiplier = 0;

    if (hasServiceFeePreDeducted && mode === 'positiveEquity') {
      serviceFeeMultiplier = isNearNegativeEquity ? 0 : -1;
    }
    return serviceFeeMultiplier;
  }, [hasServiceFeePreDeducted, isNearNegativeEquity, mode]);

  const isPaymentCompleted = hasServiceFeePreDeducted && mode === 'saleComplete';
  const isNegativeEquity = mode === 'negativeEquity';
  const isNearNegativeEquityCompleted = isPaymentCompleted && isNearNegativeEquity;

  const serviceFeeDisclaimer = isNegativeEquity || isNearNegativeEquity
    ? localT.translate('labels.serviceFeeDisclaimer.link') as string
    : `${localT.translate('labels.serviceFeeDisclaimer.auto') as string} ${localT.translate('labels.serviceFeeDisclaimer.link') as string}`;

  const serviceFeeAmount = useMemo(() => (
    <span className={styles.feeAmountWrapper}>
      {formatPriceTransactional(-Math.abs(feeAmountGBP ?? 0))}
      <span
        aria-hidden="true"
        className={cx(styles.loadingFee, { [styles.finishedLoading]: isFeeLoaded })}
      />
    </span>
  ), [feeAmountGBP, isFeeLoaded]);

  const serviceFeeDetailsItems = useMemo(() => [
    {
      className: mode !== 'saleComplete' && styles.financingAmount,
      id: 'financingAmountFormatted',
      node: (
        <SaleDetailsItem
          // ensure we display finance figure as negative number
          amount={formatPriceTransactional(-Math.abs(financeFigure!))}
          label={localT.translate('labels.financingAmountFormatted')}
        />
      ),
      shouldShow: validateHasTransactionalValue(financeFigure),
    },
    {
      id: 'soldForFigure',
      node: <SaleDetailsItem amount={formatPriceTransactional(soldForFigure!)} label={localT.translate('labels.soldForFigure')} />,
      shouldShow: validateFigure(soldForFigure),
    },
    {
      id: 'serviceFee',
      node: (
        <SaleDetailsItem
          amount={serviceFeeAmount}
          label={
            <ServiceFeeAppliesNotice
              label={localT.translate('labels.serviceFee') as string}
              serviceFeeTiers={serviceFeeTiers ?? undefined}
              showFee={false}
            />
          }
        />
      ),
      shouldShow: isPaymentCompleted && !isNearNegativeEquityCompleted,
    },
    {
      className: styles.homeCollection,
      id: 'homeCollection',
      node: (
        <SaleDetailsItem
          amount={<Badge text={localT.translate('included')} variant={BADGE_VARIANT.PRIMARY} />}
          label={localT.translate('labels.collection')}
        />
      ),
      shouldShow: showHomeCollectionLineItem,
    },
    {
      className: styles.sellerAmount,
      id: 'sellerAmountFormatted',
      node: (
        <SaleDetailsItem
          amount={formatPriceTransactional(sellerFigure! - getServiceFeeMultiplier() * (feeAmountGBP ?? 0))}
          label={
            <div className={styles.withPayoutDetails}>
              {mode === 'saleComplete' ? (
                localT.translate('labels.total')
              ) : (
                <IconText
                  data-thc-icontext
                  reverse
                  icon={SellerAmountFormattedIcon}
                  text={localT.translate(`labels.${mode === 'negativeEquity' ? 'sellerAmountNegativeEquity' : 'estimatedPayout'}`)}
                  variant="small--heading5"
                />
              )}
              <PayoutDetails {...{ showPayoutDetails }} />
            </div>
          }
        />
      ),
      shouldShow: validateFigure(sellerFigure),
    },
    {
      className: styles.totalPaid,
      id: 'totalPaid',
      node: (
        <SaleDetailsItem
          amount={formatPriceTransactional(bankTransferFigure! - getServiceFeeMultiplier() * (feeAmountGBP ?? 0))}
          label={<IconText
            data-thc-icontext
            icon={BankIcon}
            text={localT.translate('labels.bankTransfer')}
            variant="xsmall--small"
          />}
        />
      ),
      shouldShow: validateHasTransactionalValue(bankTransferFigure),
    },
    {
      id: 'serviceFee',
      node: (
        <div className={styles.serviceFeeWrapper}>
          <SaleDetailsItem
            amount={serviceFeeAmount}
            label={
              <ServiceFeeAppliesNotice
                label={localT.translate('labels.serviceFee') as string}
                serviceFeeTiers={serviceFeeTiers ?? undefined}
                showFee={false}
              />
            }
          />
          <span className={styles.serviceFeeDisclaimer}>{serviceFeeDisclaimer}</span>
        </div>
      ),
      shouldShow: !isPaymentCompleted || isNearNegativeEquityCompleted,
    },
  ], [
    bankTransferFigure, feeAmountGBP, financeFigure,
    getServiceFeeMultiplier, isNearNegativeEquityCompleted,
    isPaymentCompleted, mode,
    sellerFigure, serviceFeeAmount,
    serviceFeeDisclaimer, serviceFeeTiers,
    showHomeCollectionLineItem, showPayoutDetails, soldForFigure,
  ]);

  return (
    <div className={styles.root} {...dataProps}>
      <CardShell {...{ title, titleIcon }}>
        {showAsError ? (
          <SaleDetailsError />
        ) : (
          <CardContent
            lineItems={serviceFeeDetailsItems}
            {...{
              showOverpaymentMessage,
              showPayoutDetails,
              showVehicleCard,
              variant,
            }}
          />
        )}
      </CardShell>
    </div>
  );
};
